import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

let hasRegisteredPlugin = false;

function registerScrollTrigger() {
  if (hasRegisteredPlugin) {
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  hasRegisteredPlugin = true;
}

function getViewportPanelHeight() {
  return Math.max(window.innerHeight, 1);
}

function splitElementIntoLines(element) {
  if (!element) {
    return [];
  }

  var hasNestedElements = element.children && element.children.length > 0;

  if (hasNestedElements && !element.classList.contains('differentiators__heading')) {
    return [];
  }

  var originalText = (element.textContent || '').replace(/\s+/g, ' ').trim();

  if (!originalText) {
    return [];
  }

  element.setAttribute('data-differentiators-split-text', originalText);
  element.textContent = '';

  var words = originalText.split(' ');

  words.forEach(function appendWord(word, index) {
    var wordElement = document.createElement('span');
    wordElement.className = 'differentiators__line-word';
    wordElement.textContent = index < words.length - 1 ? word + '\u00A0' : word;
    element.appendChild(wordElement);
  });

  var wordElements = Array.prototype.slice.call(element.querySelectorAll('.differentiators__line-word'));

  if (!wordElements.length) {
    return [];
  }

  var groupedLines = [];
  var currentTop = null;

  wordElements.forEach(function collectWordLine(wordElement) {
    var wordTop = Math.round(wordElement.offsetTop);

    if (null === currentTop || Math.abs(wordTop - currentTop) > 2) {
      groupedLines.push([]);
      currentTop = wordTop;
    }

    groupedLines[groupedLines.length - 1].push(wordElement);
  });

  var linesFragment = document.createDocumentFragment();
  var lineInnerElements = [];

  groupedLines.forEach(function appendLine(lineWordElements) {
    var lineMask = document.createElement('span');
    var lineInner = document.createElement('span');

    lineMask.className = 'differentiators__line-mask';
    lineInner.className = 'differentiators__line-inner';

    lineWordElements.forEach(function appendLineWord(lineWordElement) {
      lineInner.appendChild(lineWordElement);
    });

    lineMask.appendChild(lineInner);
    linesFragment.appendChild(lineMask);
    lineInnerElements.push(lineInner);
  });

  element.textContent = '';
  element.appendChild(linesFragment);

  return lineInnerElements;
}

function revertSplitElement(element) {
  if (!element) {
    return;
  }

  var originalText = element.getAttribute('data-differentiators-split-text');

  if (null === originalText) {
    return;
  }

  element.textContent = originalText;
  element.removeAttribute('data-differentiators-split-text');
}

function preparePanelTextAnimations(contentPanels) {
  var splitElements = [];

  var panelTextData = contentPanels.map(function mapPanelText(panelElement) {
    var headingElement = panelElement.querySelector('.differentiators__heading');
    var bodyParagraphs = Array.prototype.slice.call(panelElement.querySelectorAll('.differentiators__body p'));
    var headingLines = splitElementIntoLines(headingElement);
    var bodyLines = [];
    var fallbackBodyElements = [];

    if (headingLines.length && headingElement) {
      splitElements.push(headingElement);
    }

    bodyParagraphs.forEach(function splitBodyParagraph(paragraphElement) {
      var paragraphLines = splitElementIntoLines(paragraphElement);

      if (paragraphLines.length) {
        splitElements.push(paragraphElement);
        bodyLines = bodyLines.concat(paragraphLines);
        return;
      }

      fallbackBodyElements.push(paragraphElement);
    });

    return {
      headingLines: headingLines,
      bodyLines: bodyLines,
      fallbackBodyElements: fallbackBodyElements,
    };
  });

  function cleanupTextSplits() {
    splitElements.forEach(function revertSplit(splitElement) {
      revertSplitElement(splitElement);
    });
  }

  return {
    panelTextData: panelTextData,
    cleanup: cleanupTextSplits,
  };
}

function setPanelTextState(panelTextData, activeIndex) {
  panelTextData.forEach(function applyTextState(panelData, panelIndex) {
    var isActivePanel = panelIndex === activeIndex;

    if (panelData.headingLines.length) {
      gsap.set(panelData.headingLines, {
        yPercent: isActivePanel ? 0 : 100,
      });
    }

    if (panelData.bodyLines.length) {
      gsap.set(panelData.bodyLines, {
        yPercent: isActivePanel ? 0 : 100,
      });
    }

    if (panelData.fallbackBodyElements.length) {
      gsap.set(panelData.fallbackBodyElements, {
        autoAlpha: isActivePanel ? 1 : 0,
        y: isActivePanel ? 0 : 16,
      });
    }
  });
}

function addPanelTextReveal(timeline, panelTextData, panelIndex, startTime) {
  var panelData = panelTextData[panelIndex];

  if (!panelData) {
    return;
  }

  if (panelData.headingLines.length) {
    timeline.set(panelData.headingLines, { yPercent: 100 }, startTime);
    timeline.to(panelData.headingLines, {
      yPercent: 0,
      duration: 0.85,
      stagger: 0.1,
      ease: 'power4.out',
    }, startTime + 0.08);
  }

  if (panelData.bodyLines.length) {
    timeline.set(panelData.bodyLines, { yPercent: 100 }, startTime);
    timeline.to(panelData.bodyLines, {
      yPercent: 0,
      duration: 0.8,
      stagger: 0.08,
      ease: 'power4.out',
    }, startTime + 0.22);
  }

  if (panelData.fallbackBodyElements.length) {
    timeline.set(panelData.fallbackBodyElements, {
      autoAlpha: 0,
      y: 16,
    }, startTime);
    timeline.to(panelData.fallbackBodyElements, {
      autoAlpha: 1,
      y: 0,
      duration: 0.7,
      stagger: 0.08,
      ease: 'power4.out',
    }, startTime + 0.22);
  }
}

function setPanelState(primaryPanels, contentPanels, activeIndex) {
  primaryPanels.forEach(function applyPrimaryPanelState(panelElement, panelIndex) {
    panelElement.classList.toggle('is-active', panelIndex === activeIndex);
  });

  contentPanels.forEach(function applyContentPanelState(panelElement, panelIndex) {
    panelElement.classList.toggle('is-active', panelIndex === activeIndex);
  });
}

function createPinnedDifferentiatorsTrigger(sectionElement, panelCount, handleThresholdUpdate) {
  return ScrollTrigger.create({
    trigger: sectionElement,
    start: 'top top',
    end: function end() {
      var panelTravel = getViewportPanelHeight();
      var scrollDistance = Math.max(panelTravel * (panelCount - 1), 1);
      return '+=' + Math.round(scrollDistance);
    },
    pin: true,
    anticipatePin: 1,
    invalidateOnRefresh: true,
    onToggle: function onToggle(self) {
      sectionElement.classList.toggle('home-section--differentiators-pinned-active', self.isActive);
    },
    onUpdate: function onUpdate(self) {
      var thresholdIndex = Math.max(0, Math.min(panelCount - 1, Math.round(self.progress * (panelCount - 1))));
      handleThresholdUpdate(thresholdIndex);
    },
  });
}

function createPanelTransitionTimeline(params) {
  var primaryPanels = params.primaryPanels;
  var contentPanels = params.contentPanels;
  var panelTextData = params.panelTextData;
  var fromIndex = params.fromIndex;
  var toIndex = params.toIndex;

  var previousPrimaryPanel = primaryPanels[fromIndex];
  var nextPrimaryPanel = primaryPanels[toIndex];
  var previousContentPanel = contentPanels[fromIndex];
  var nextContentPanel = contentPanels[toIndex];

  var transitionTimeline = gsap.timeline({
    defaults: {
      overwrite: true,
    },
  });

  transitionTimeline.set([previousPrimaryPanel, previousContentPanel], {
    autoAlpha: 1,
  }, 0);

  transitionTimeline.set([nextPrimaryPanel, nextContentPanel], {
    autoAlpha: 0,
  }, 0);

  transitionTimeline.to([previousPrimaryPanel, previousContentPanel], {
    autoAlpha: 0,
    duration: 0.6,
    ease: 'power2.inOut',
  }, 0);

  transitionTimeline.to([nextPrimaryPanel, nextContentPanel], {
    autoAlpha: 1,
    duration: 0.6,
    ease: 'power2.inOut',
  }, 0);

  addPanelTextReveal(transitionTimeline, panelTextData, toIndex, 0);

  transitionTimeline.call(setPanelState, [primaryPanels, contentPanels, toIndex], 0.6);

  return transitionTimeline;
}

export function initializeDifferentiatorsPinnedScroll() {
  if (typeof window === 'undefined') {
    return function noop() {};
  }

  registerScrollTrigger();

  var sections = Array.prototype.slice.call(document.querySelectorAll('.home-section--differentiators'));

  if (!sections.length) {
    return function noop() {};
  }

  var cleanups = [];

  sections.forEach(function setupSection(sectionElement) {
    var contentElement = sectionElement.querySelector('.differentiators__content');
    var primaryStackElement = sectionElement.querySelector('.differentiators__primary-stack');
    var trackElement = sectionElement.querySelector('.differentiators__content-track');
    var itemElements = trackElement ? Array.prototype.slice.call(trackElement.querySelectorAll('.differentiators__item')) : [];
    var primaryPanels = primaryStackElement ? Array.prototype.slice.call(primaryStackElement.querySelectorAll('.differentiators__primary-wrap')) : [];
    var panelCount = Math.min(itemElements.length, primaryPanels.length);

    if (!contentElement || !trackElement || panelCount < 1) {
      return;
    }

    function clearPanelAnimationProps() {
      gsap.set(primaryPanels, { clearProps: 'opacity,visibility,will-change' });
      gsap.set(itemElements, { clearProps: 'opacity,visibility,will-change' });
    }

    function resetPanelsToFirst() {
      setPanelState(primaryPanels, itemElements, 0);
      clearPanelAnimationProps();
    }

    resetPanelsToFirst();

    var mediaMatcher = gsap.matchMedia();

    mediaMatcher.add({
      reduceMotion: '(prefers-reduced-motion: reduce)',
      desktop: '(min-width: 1025px)',
      tablet: '(min-width: 768px) and (max-width: 1024px)',
      mobile: '(max-width: 767px)',
    }, function setupPinnedTimeline(context) {
      var conditions = context.conditions;

      if (conditions.reduceMotion || conditions.mobile || panelCount < 2) {
        sectionElement.classList.remove('home-section--differentiators-pinned');
        sectionElement.classList.remove('home-section--differentiators-pinned-active');
        resetPanelsToFirst();
        return undefined;
      }

      sectionElement.classList.add('home-section--differentiators-pinned');
      gsap.set(primaryPanels, { willChange: 'opacity' });
      gsap.set(itemElements, { willChange: 'opacity' });

      var textPreparation = preparePanelTextAnimations(itemElements);
      var panelTextData = textPreparation.panelTextData;
      var currentIndex = 0;
      var pendingIndex = 0;
      var activeTransition = null;

      setPanelTextState(panelTextData, 0);
      setPanelState(primaryPanels, itemElements, 0);
      gsap.set(primaryPanels, { autoAlpha: 0 });
      gsap.set(itemElements, { autoAlpha: 0 });
      gsap.set([primaryPanels[0], itemElements[0]], { autoAlpha: 1 });

      function runPendingTransition() {
        if (activeTransition || pendingIndex === currentIndex) {
          return;
        }

        var fromIndex = currentIndex;
        var toIndex = pendingIndex;

        activeTransition = createPanelTransitionTimeline({
          primaryPanels: primaryPanels,
          contentPanels: itemElements,
          panelTextData: panelTextData,
          fromIndex: fromIndex,
          toIndex: toIndex,
        });

        activeTransition.eventCallback('onComplete', function handleTransitionComplete() {
          currentIndex = toIndex;
          activeTransition = null;
          runPendingTransition();
        });
      }

      function requestPanelByThreshold(targetIndex) {
        if (targetIndex === pendingIndex && targetIndex === currentIndex) {
          return;
        }

        pendingIndex = targetIndex;
        runPendingTransition();
      }

      var trigger = createPinnedDifferentiatorsTrigger(sectionElement, panelCount, requestPanelByThreshold);

      return function cleanupPinnedTimeline() {
        if (activeTransition) {
          activeTransition.kill();
          activeTransition = null;
        }

        trigger.kill();
        sectionElement.classList.remove('home-section--differentiators-pinned-active');
        textPreparation.cleanup();
        resetPanelsToFirst();
      };
    });

    cleanups.push(function cleanupSection() {
      mediaMatcher.revert();
      sectionElement.classList.remove('home-section--differentiators-pinned');
      sectionElement.classList.remove('home-section--differentiators-pinned-active');
      resetPanelsToFirst();
    });
  });

  return function cleanupAllDifferentiatorSections() {
    cleanups.forEach(function runCleanup(cleanup) {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    });

    ScrollTrigger.refresh();
  };
}
